// Report data assembly for the analytics PDF export.
//
// The export is a deliberate user action, so each report re-fetches its
// inputs fresh via the same api-client functions the page already uses
// (getLocationAnalytics / getOrgAnalytics / getTicketTrends). This decouples
// the PDF from whatever happens to be mounted in the React tree (the trends
// query lives inside TrendsSection, not the page) and guarantees the export
// reflects the current server state for the selected range. Re-fetch cost is
// negligible next to PDF generation and the button already shows a spinner.

import { getLocationAnalytics, getOrgAnalytics, getTicketTrends } from '@jq4race/api-client';
import type {
  LocationAnalyticsResponse,
  OrgAnalyticsResponse,
  TicketTrendsResponse,
} from '@jq4race/shared';

export interface LocationReportData {
  locationName: string;
  /**
   * Range echoed into the report header. `gatherLocationReport` sets it to the
   * calendar days the admin picked (the location's days, 'YYYY-MM-DD'), NOT the
   * UTC instants sent to the API — those would label the last day as the next one.
   */
  from: string;
  to: string;
  kpi: LocationAnalyticsResponse;
  trends: TicketTrendsResponse;
}

export interface OrgReportData {
  from: string;
  to: string;
  org: OrgAnalyticsResponse;
  trends: TicketTrendsResponse;
  /** locationId → display name, for the per-location breakdown table. */
  locationNames: Record<string, string>;
}

export async function gatherLocationReport(params: {
  locationId: string;
  locationName: string;
  /** UTC instants bounding the location's local days, sent to the API. */
  from: string;
  to: string;
  /** The calendar days (location-local) to print in the report header. */
  displayRange?: { from: string; to: string };
}): Promise<LocationReportData> {
  const { locationId, locationName, from, to, displayRange } = params;
  const [kpi, trends] = await Promise.all([
    getLocationAnalytics(locationId, { from, to }),
    getTicketTrends({ locationId, from, to }),
  ]);
  return {
    locationName,
    from: displayRange?.from ?? from,
    to: displayRange?.to ?? to,
    kpi,
    trends,
  };
}

export async function gatherOrgReport(params: {
  from: string;
  to: string;
  locationNames: Record<string, string>;
}): Promise<OrgReportData> {
  const { from, to, locationNames } = params;
  const [org, trends] = await Promise.all([
    getOrgAnalytics({ from, to }),
    getTicketTrends({ from, to }),
  ]);
  return { from, to, org, trends, locationNames };
}
